import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Admin {
  id: string;
  email: string;
  whiteLabelClientId: string;
  clientName: string;
}

interface AuthContextType {
  admin: Admin | null;
  token: string | null;
  login: (admin: Admin, token: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const storedAdmin = localStorage.getItem('wl_admin');
    const storedToken = localStorage.getItem('wl_admin_token');

    if (storedAdmin && storedToken) {
      setAdmin(JSON.parse(storedAdmin));
      setToken(storedToken);
    }
  }, []);

  const login = (adminData: Admin, tokenData: string) => {
    setAdmin(adminData);
    setToken(tokenData);
    localStorage.setItem('wl_admin', JSON.stringify(adminData));
    localStorage.setItem('wl_admin_token', tokenData);
  };

  const logout = () => {
    setAdmin(null);
    setToken(null);
    localStorage.removeItem('wl_admin');
    localStorage.removeItem('wl_admin_token');
  };

  return (
    <AuthContext.Provider
      value={{
        admin,
        token,
        login,
        logout,
        isAuthenticated: !!admin && !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
